import React, { useState, useEffect, useRef } from 'react';
import { ApiService, ELO_VISIBLE } from '../services/apiService';
import { formatRatedClockLabel } from '../utils/eloUtils';
import './ChallengeToast.css';

interface ChallengeToastProps {
  challengeId: number;
  challengerUsername: string;
  timerSettings?: { minutesPerPlayer: number; incrementSeconds: number };
  isRated?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onTimeout: () => void;
  isInGame?: boolean;
}

const ChallengeToast: React.FC<ChallengeToastProps> = ({
  challengeId,
  challengerUsername,
  timerSettings,
  isRated,
  onAccept,
  onDecline,
  onTimeout,
  isInGame = false
}) => {
  const [timeLeft, setTimeLeft] = useState(30);
  const [isProcessing, setIsProcessing] = useState(false);
  const onTimeoutRef = useRef(onTimeout);

  // Keep ref updated with latest onTimeout callback
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    console.log('🎯 ChallengeToast mounted/updated:', { challengeId, challengerUsername, timeLeft });
    
    // Countdown timer - use ref to avoid dependency on onTimeout
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          console.log('⏰ ChallengeToast timer expired, calling onTimeout');
          onTimeoutRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      console.log('🧹 ChallengeToast cleanup - clearing timer');
      clearInterval(timer);
    };
  }, [challengeId, challengerUsername]); // Only re-run if challenge changes, not onTimeout

  const handleAccept = async () => {
    if (isProcessing) return;
    
    // Don't allow accepting if already in a game
    if (isInGame) {
      alert('You cannot accept a challenge while already in a game');
      return;
    }
    
    setIsProcessing(true);
    try {
      const result = await ApiService.acceptChallenge(challengeId);
      if (result.success) {
        onAccept();
      } else {
        alert(result.error || 'Failed to accept challenge');
        setIsProcessing(false);
      }
    } catch (err) {
      alert('Failed to accept challenge');
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await ApiService.declineChallenge(challengeId);
      onDecline();
    } catch (err) {
      alert('Failed to decline challenge');
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Backdrop overlay - dims background and prevents interaction */}
      <div className="challenge-toast-overlay" />
      
      {/* Challenge modal */}
      <div className="challenge-toast">
        <div className="challenge-toast-content">
          <div className="challenge-toast-header">
            <span className="challenge-icon">🎮</span>
            <h3>Challenge Received!</h3>
          </div>
          <p className="challenge-message">
            <strong>{challengerUsername}</strong> challenged you to a game!
          </p>
          {timerSettings && (
            <div style={{ fontSize: '0.9rem', color: '#495057', textAlign: 'center', margin: '8px 0' }}>
              ⏱️ Time Control: <strong>{timerSettings.minutesPerPlayer} minutes</strong>
              {timerSettings.incrementSeconds > 0 && ` + ${timerSettings.incrementSeconds}s increment`}
            </div>
          )}
          {ELO_VISIBLE && (
            <div style={{ fontSize: '0.85rem', textAlign: 'center', margin: '4px 0' }}>
              <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: isRated ? '#667eea' : '#6c757d', color: 'white', fontWeight: 'bold' }}>
                {formatRatedClockLabel(timerSettings?.minutesPerPlayer, timerSettings?.incrementSeconds, isRated !== false)}
              </span>
            </div>
          )}
          <div className="challenge-timer">
            Auto-decline in: <span className="timer-value">{timeLeft}s</span>
          </div>
          <div className="challenge-actions">
            <button
              className="challenge-accept-btn"
              onClick={handleAccept}
              disabled={isProcessing}
            >
              ✓ Accept
            </button>
            <button
              className="challenge-decline-btn"
              onClick={handleDecline}
              disabled={isProcessing}
            >
              ✗ Decline
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChallengeToast;


